import com.github.chosamuel.kishibe.application.Application
import com.github.chosamuel.kishibe.application.getHeight
import com.github.chosamuel.kishibe.application.getWidth
import com.github.chosamuel.kishibe.application.renderer.Color
import com.github.chosamuel.kishibe.application.renderer.circle
import com.github.chosamuel.kishibe.application.renderer.clear
import com.github.chosamuel.kishibe.application.renderer.fill
import com.github.chosamuel.kishibe.color.cosineColor
import com.github.chosamuel.kishibe.graphics.Mesh
import com.github.chosamuel.kishibe.math.Vector2
import com.github.chosamuel.kishibe.math.map
import com.github.chosamuel.kishibe.noise.simplex
import com.github.chosamuel.kishibe.ui.Radio
import com.github.chosamuel.kishibe.ui.XYPad
import org.khronos.webgl.WebGLRenderingContext
import kotlin.math.abs
import kotlin.math.sin
import kotlin.math.sqrt

val xy = XYPad().apply {
    this.easingRate = 0.069
}
val radio = Radio(numBtns = 4, topPadding = 25.0)

var theta = 0.0
var difference = 0.0

fun main() {
    xy.appendToDocument()
    radio.appendToDocument()
    console.log("""
        PHOSPHENE/SPECTRAL EYE 2021
        Made by Tsao
        with love 
        
        Twitter: @_samuelcho
        Instagram: @_samuelcho_
    """.trimIndent())
}

val app = Application {
    draw {
        easeColors()
        clear(0.0)
        val gs = getWidth()*0.608
        val xpad = (getWidth() - gs) * 0.5
        val ypad = (getHeight() - gs) * 0.5
        val units = 30
        val spacing = gs / units
        val m = Mesh(gl, this)
        val lfo = xy.x * xy.x
        val noiseLfo = map(0.0,1.0,0.005,0.025,xy.y)
        difference += ((abs(xy.dx) + abs(xy.dy)) - difference) * 0.01
        theta += abs(difference)
        for(y in 0..units.toInt()){
            for(x in 0..units.toInt()){
                fill(1.0, 1.0, 1.0)
                val pos = Vector2(x*spacing + xpad, y*spacing + ypad)
                    .minus(Vector2(getWidth()*0.5, getHeight()*0.5))
                    .div(gs*(0.5+lfo))
                    .let { mapCircle(it.x,it.y) }
                    .times(gs*(0.5+lfo))
                    .rotate(theta * 0.0025)
                    .plus(Vector2(getWidth()*0.5, getHeight()*0.5))
                val c = simplex(x*noiseLfo,y*noiseLfo,frameCount*0.005)
                    .let{it*0.5+0.5}
                val col = cosineColor(c + frameCount*0.005,
                    currentA,
                    currentB,
                    currentC,
                    currentD
                )
                m.addColor(Color(col[0],col[1],col[2]))
                m.addVertex(pos)
            }
        }
        for(y in 0 until units){
            for(x in 0 until units){
                val w = (units+1).toShort()
                val i1 = (x + y * w).toShort()
                val i2 = ((x+1) + y * w).toShort()
                val i3 = (x + (y+1) * w).toShort()
                val i4 = ((x+1) + (y+1) * w).toShort()
                m.addIndex(i1)
                m.addIndex(i2)
                m.addIndex(i3)
                m.addIndex(i2)
                m.addIndex(i4)
                m.addIndex(i3)
            }
        }
        m.draw(WebGLRenderingContext.TRIANGLES)
    }
}

fun mapCircle(x: Double, y:Double): Vector2 {
    return Vector2(
        x * sqrt(1.0 - y * y / 2.0),
        y * sqrt(1.0 - x * x / 2.0)
    )
}
var currentA = mutableListOf(0.5,0.5,0.5)
var currentB = mutableListOf(0.5,0.5,0.5)
var currentC = mutableListOf(1.0,0.7,0.4)
var currentD = mutableListOf(0.0,0.15,0.2)
val aVals = listOf(
    listOf(0.5,0.5,0.5),
    listOf(0.5,0.5,0.5),
    listOf(0.8,0.5,0.4),
    listOf(0.5,0.5,0.5),
)
val bVals = listOf(
    listOf(0.5,0.5,0.5),
    listOf(0.5,0.5,0.5),
    listOf(0.2,0.4,0.2),
    listOf(0.5,0.5,0.5),
)
val cVals = listOf(
    listOf(1.0,0.7,0.4),
    listOf(1.0,1.0,1.0),
    listOf(2.0,1.0,1.0),
    listOf(1.0,1.0,1.0),
)
val dVals = listOf(
    listOf(0.0,0.15,0.20),
    listOf(0.0,0.10,0.20),
    listOf(0.5,0.2,0.25),
    listOf(0.0,0.0,0.0),
)
fun easeColors(){
    val ease = 0.1
    repeat(3){ i ->
        currentA[i] += (aVals[radio.mode][i] - currentA[i]) * ease
        currentB[i] += (bVals[radio.mode][i] - currentB[i]) * ease
        currentC[i] += (cVals[radio.mode][i] - currentC[i]) * ease
        currentD[i] += (dVals[radio.mode][i] - currentD[i]) * ease
    }
}

